import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Heart, Receipt, ShieldCheck, Plus, Minus, CheckCircle } from "@phosphor-icons/react";
import Header from "../components/Header";
import FeedbackCTA from "../components/FeedbackCTA";
import { SAVE_PAGES, buildFaqJsonLd } from "../lib/savePagesData";

/**
 * /save/:slug — shared landing page for the three core pillars
 * (wishlist, price-claim, warranty). One reusable component, content
 * driven by `savePagesData.js`.
 *
 * Purpose: own the long-tail "how to save money on X in NZ" Google
 * queries for each pillar individually, so SERPs send specific
 * intent to the specific landing page instead of dumping everyone on
 * the homepage. Pattern mirrors `RetailerLanding.jsx`.
 *
 * SEO surface this page contributes:
 *  - Unique <title>, <meta name="description">, canonical, og:* tags.
 *  - schema.org FAQPage JSON-LD for rich-result eligibility.
 *  - Strong H1 + H2s with naturally-occurring money-saving keywords.
 *  - Internal cross-links between the three pillars to spread link
 *    equity across the cluster.
 */

const PILLAR_ICON = {
  wishlist: Heart,
  "price-claim": Receipt,
  warranty: ShieldCheck,
};

export default function SavePage() {
  const { slug } = useParams();
  const data = SAVE_PAGES[slug];
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = data.metaTitle;

    /**
     * Tiny helper: set or update a <meta> tag, returning a function
     * that restores the previous value (so navigating away doesn't
     * leak the pillar's meta into the next page's <head>).
     */
    const setMeta = (selector, attr, value) => {
      let tag = document.head.querySelector(selector);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("meta");
        const [, kind] = selector.match(/\[(.*?)=/) || [];
        const [, name] = selector.match(/="(.*?)"/) || [];
        if (kind && name) tag.setAttribute(kind, name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute(attr);
      tag.setAttribute(attr, value);
      return () => {
        if (created) tag.remove();
        else if (prev !== null) tag.setAttribute(attr, prev);
      };
    };

    const restoreDesc = setMeta('meta[name="description"]', "content", data.metaDescription);
    const restoreKeywords = setMeta('meta[name="keywords"]', "content", data.keywords.join(", "));
    const restoreOgTitle = setMeta('meta[property="og:title"]', "content", data.metaTitle);
    const restoreOgDesc = setMeta('meta[property="og:description"]', "content", data.metaDescription);
    const restoreOgUrl = setMeta('meta[property="og:url"]', "content", `https://priceguard.nz/save/${data.slug}`);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://priceguard.nz/save/${data.slug}`);

    // FAQPage JSON-LD for rich results
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.text = JSON.stringify(buildFaqJsonLd(data));
    ldScript.dataset.savePageFaq = data.slug;
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      restoreDesc();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDesc();
      restoreOgUrl();
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
      ldScript.remove();
    };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;
  const Icon = PILLAR_ICON[data.slug];

  return (
    <div className="min-h-screen bg-white" data-testid={`save-page-${data.slug}`}>
      <Header />

      {/* HERO */}
      <section className="border-b border-[#e4e4e7]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 pg-overline mb-4" style={{ color: data.accent }} data-testid="save-page-overline">
              <span className="w-6 h-6 flex items-center justify-center" style={{ background: data.accentBg }}>
                <Icon size={12} weight="fill" color={data.accent} />
              </span>
              {data.introOverline}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]" data-testid="save-page-h1">
              {data.h1Top}<br />
              <span style={{ color: data.accent }}>{data.h1Bottom}</span>
            </h1>
            <p className="text-base sm:text-lg text-[#52525b] leading-relaxed mt-6 max-w-2xl" data-testid="save-page-intro">
              {data.introBody}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={data.cta.primaryHref} className="font-bold px-7 py-4 inline-flex items-center gap-2 text-white transition-colors hover:opacity-90" style={{ background: data.accent }} data-testid="save-page-primary-cta">
                {data.cta.primary} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to={data.cta.secondaryHref} className="border border-black font-bold px-7 py-4 inline-flex items-center gap-2 hover:bg-black hover:text-white transition-colors" data-testid="save-page-secondary-cta">
                {data.cta.secondary}
              </Link>
            </div>
            <p className="text-xs font-mono-pg text-[#52525b] uppercase tracking-widest mt-5">Free forever for up to 5 items · no credit card needed</p>
          </div>

          {/* Right-column metric card. Keeps the page feeling like a
              product page rather than a blog post by anchoring an actual
              number from our launch data. */}
          <div className="lg:col-span-5">
            <div className="p-7 lg:p-8 border border-[#e4e4e7] bg-[#f7f7f8] lg:sticky lg:top-24" data-testid="save-page-metric">
              <div className="pg-overline" style={{ color: data.accent }}>The proof</div>
              <div className="font-heading text-6xl lg:text-7xl font-black tracking-tighter mt-3" style={{ color: data.accent }}>
                {data.metric.value}
              </div>
              <div className="font-heading text-lg font-bold tracking-tight mt-2">{data.metric.label}</div>
              <div className="text-xs font-mono-pg text-[#52525b] uppercase tracking-widest mt-1">{data.metric.sub}</div>
              <div className="mt-6 pt-6 border-t border-[#e4e4e7] text-xs text-[#52525b] leading-relaxed">
                Illustrative based on our launch user data · individual results depend on retailer policy and stock movement · the call is always the retailer's.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKED EXAMPLES */}
      <section className="border-b border-[#e4e4e7] bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="pg-overline mb-3">Worked examples · real NZ retailers</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">
              Three Kiwi households, three saves.
            </h2>
            <p className="text-[#52525b] mt-4 leading-relaxed">
              Indicative examples drawn from PriceGuard's launch data and public NZ retailer pricing. Names are anonymised, the numbers and retailers are real.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
            {data.examples.map((ex, i) => (
              <div key={ex.title} className="bg-white p-6" data-testid={`save-page-example-${i}`}>
                <div className="pg-overline" style={{ color: data.accent }}>{ex.retailer}</div>
                <h3 className="font-heading text-xl font-bold tracking-tight mt-2">{ex.title}</h3>
                <div className="mt-5 flex items-baseline gap-3">
                  <span className="font-mono-pg text-sm text-[#52525b] line-through">{ex.full}</span>
                  <span className="font-heading text-xl font-black tracking-tight">{ex.sale}</span>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 font-heading text-2xl font-black tracking-tighter" style={{ color: data.accent }}>
                  {ex.save} <span className="text-xs font-mono-pg uppercase tracking-widest text-[#52525b]">saved</span>
                </div>
                <p className="text-sm text-[#52525b] mt-4 leading-relaxed">{ex.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-[#e4e4e7] bg-[#f7f7f8]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="pg-overline mb-3">How {data.pillar.toLowerCase()} works on PriceGuard</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">
              Three steps, ninety seconds, done.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
            {data.howItWorks.map((s) => (
              <div key={s.n} className="bg-white p-7" data-testid={`save-page-step-${s.n}`}>
                <div className="pg-overline" style={{ color: data.accent }}>Step {s.n}</div>
                <h3 className="font-heading text-xl font-bold tracking-tight mt-3">{s.t}</h3>
                <p className="text-sm text-[#52525b] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — also doubles as our FAQPage schema.org payload. */}
      <section className="border-b border-[#e4e4e7] bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="pg-overline mb-3">Frequently asked</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
                Real questions<br />from real Kiwi shoppers.
              </h2>
              <p className="text-[#52525b] mt-4 text-sm leading-relaxed max-w-md">
                Answered plainly. If a friend asked you over coffee, this is what they'd want to know.
              </p>
            </div>
            <div className="lg:col-span-8 border border-[#e4e4e7] divide-y divide-[#e4e4e7]">
              {data.faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} data-testid={`save-page-faq-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      aria-expanded={open}
                      className="w-full flex items-start justify-between gap-4 text-left px-5 py-5 hover:bg-[#f7f7f8] transition-colors"
                      data-testid={`save-page-faq-toggle-${i}`}
                    >
                      <span className="font-heading font-bold text-lg tracking-tight leading-snug">{f.q}</span>
                      <span className="flex-shrink-0 mt-1">{open ? <Minus size={18} weight="bold" /> : <Plus size={18} weight="bold" />}</span>
                    </button>
                    {open && (
                      <div className="px-5 pb-5 text-sm text-[#52525b] leading-relaxed">{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CROSS-LINK + CTA STRIP */}
      <section className="border-b border-[#e4e4e7] bg-[#0a0a0a] text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="pg-overline text-white/60 mb-3">Three jobs, one app</div>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
                Whatever stage you're at, we've got the next move.
              </h2>
              <p className="text-white/70 mt-4 leading-relaxed max-w-xl">
                PriceGuard works across the full life of a purchase - before you buy, just after you buy, and years into ownership. Every part is free to start.
              </p>
              <div className="mt-6 space-y-3" data-testid="save-page-cross-links">
                {data.crossLinks.map((cl) => (
                  <Link key={cl.href} to={cl.href} className="flex items-center justify-between gap-4 border border-white/15 p-4 hover:bg-white/5 transition-colors group">
                    <span className="font-bold">{cl.label}</span>
                    <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 border border-white/15 p-7">
              <div className="pg-overline text-white/60">Start now</div>
              <h3 className="font-heading text-2xl font-black tracking-tight mt-2">
                {data.cta.primary}.
              </h3>
              <ul className="mt-5 space-y-2.5 text-sm">
                <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" color={data.accent} /> Free forever for up to 5 items</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" color={data.accent} /> No credit card, no card-on-file</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" color={data.accent} /> Independent NZ tool · not owned by a retailer</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" color={data.accent} /> All three pillars unlock the moment you sign up</li>
              </ul>
              <Link to={data.cta.primaryHref} className="mt-7 inline-flex items-center gap-2 w-full justify-center font-bold px-7 py-4 transition-colors hover:opacity-90 text-white" style={{ background: data.accent }} data-testid="save-page-bottom-cta">
                {data.cta.primary} <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FeedbackCTA />
    </div>
  );
}
